import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactElement, useMemo } from 'react';
import { ICookiesPopupProps } from '../../../components/common/CookiesPopUp';
import { IFooterProps } from '../../../components/common/layout/Footer';
import Layout from '../../../components/common/layout/Layout';
import { INavBarProps } from '../../../components/common/layout/NavBar';
import Contact, { IContactProps } from '../../../components/contact-us/Contact';
import { CountryList } from '../../../components/countries';
import { useAppSelector } from '../../../hooks';
import { PageJSONLDSchema } from '../../../models/app.models';
import {
  IContactMethod,
  IContactUsForm,
  ICountry,
  ICountryListPage,
  ICountryRegion,
  ICustomerReviewsMeta,
  IGenericServiceLanding,
  ILayoutPageProps,
  IMetaComponent,
} from '../../../models/strapi.models';
import { fetchStrapi } from '../../../services/api.service';
import { getRoutes } from '../../../shared/useBuild';
import {
  buildApiKey,
  getCommonTranslations,
  getContactMethods,
  isUndefinedOrEmpty,
  populateCookiesPopUpProps,
  populateFooterProps,
  populateNavBarProps,
} from '../../../shared/useCommon';
import {
  generateOrganizationSchema,
  generateWebsiteSchema,
} from '../../../shared/useJSONLDSchema';
import {
  addCurrencySymbol,
  applyCurrencyConversionRate,
  flatStrapiCollection,
  flatStrapiObject,
  getAlternateLinks,
  getCanonicalLink,
  getContentLanguageCode,
  getLocalizedSlugByCountryIso,
  getTranslation,
} from '../../../shared/useUtils';
import { NextPageWithLayout } from '../../_app';

interface ICopyrightCountriesPageProps extends ILayoutPageProps {
  meta: IMetaComponent;
  heading: {
    title: string;
    subTitle: string;
  };
  fromPrices: { countryCode: string; price: number }[];
  countryRegions: {
    name: string;
    countries: {
      name: string;
      code: string;
      path: any;
      services?: { name?: string; price: string }[];
    }[];
  }[];
  copyrightLandings: IGenericServiceLanding[];
  contactUsForm: IContactUsForm;
  contactUsFormSuccessMessage: string;
  contactUsFormErrorMessage: string;
  contactMethods: IContactMethod[];
  alternativeLinks: { hrefLang: string; href: string }[];
  canonicalLink: string;
}

const CopyrightCountriesPage: NextPageWithLayout<
  ICopyrightCountriesPageProps
> = (props: ICopyrightCountriesPageProps) => {
  const router = useRouter();
  const appState = useAppSelector((state) => state.app);

  const countryRegionsWithPrices = useMemo(() => {
    return props.countryRegions.map((region) => {
      region.countries = region.countries.map((country) => {
        const countryPrice =
          props.fromPrices.find((x) => x.countryCode === country.code)?.price ||
          0;
        const countryPriceWithCurrencyConversion = applyCurrencyConversionRate(
          countryPrice,
          appState.currentCurrency?.conversionRate || 1
        );

        country.services = [
          {
            price: addCurrencySymbol(
              countryPriceWithCurrencyConversion,
              appState.currentCurrency?.isoCode || 'USD',
              router.locale || 'en'
            ),
          },
        ];

        return country;
      });

      return region;
    });
  }, [
    appState.currentCurrency?.conversionRate,
    appState.currentCurrency?.isoCode,
    props.countryRegions,
    props.fromPrices,
    router.locale,
  ]);

  const contactProps: IContactProps = {
    contactMethods: props.contactMethods.map((x) => ({
      type: x.type,
      label: getTranslation(x.labelTranslations, router.locale || 'en')
        .text as string,
      value:
        x.valueTranslations && x.valueTranslations.length > 0
          ? (getTranslation(x.valueTranslations, router.locale || 'en')
              .text as string)
          : undefined,
    })),
    titleTagName: 'h2',
    formLabels: {
      title: props.contactUsForm.title,
      subTitle: props.contactUsForm.subTitle,
      name: props.contactUsForm.nameLabel,
      email: props.contactUsForm.emailLabel,
      message: props.contactUsForm.messageLabel,
      submit: props.contactUsForm.submitLabel,
      submitSuccessMessage: props.contactUsFormSuccessMessage,
      submitErrorMessage: props.contactUsFormErrorMessage,
    },
  };

  return (
    <div className="page-container">
      <Head>
        <title>{props.meta.title}</title>
        <meta name="description" content={props.meta.description} />
        <meta name="keywords" content={props.meta.keywords} />
        <link rel="canonical" href={props.canonicalLink} />
        {props.alternativeLinks.map((x, i) => (
          <link key={i} rel="alternate" hrefLang={x.hrefLang} href={x.href} />
        ))}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.pageSchema.nodes),
          }}
        />
      </Head>

      <div className="page-content-section is-narrow">
        <div className="page-content-block">
          <CountryList
            title={props.heading.title}
            description={props.heading.subTitle}
            regions={countryRegionsWithPrices}
          />
        </div>
      </div>

      <div className="page-content-section is-narrower">
        <div className="page-content-block">
          <Contact {...contactProps} />
        </div>
      </div>
    </div>
  );
};

CopyrightCountriesPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout
      locale={page.props.locale}
      navBarProps={page.props.navBarProps}
      cookiesPopUpProps={page.props.cookiesPopUpProps}
      footerProps={page.props.footerProps}
      contentLanguageCode={getContentLanguageCode(page.props.locale)}>
      {page}
    </Layout>
  );
};

export async function getStaticProps(
  context: any
): Promise<{ props: ICopyrightCountriesPageProps }> {
  const entityBaseName = 'copyright';
  const countryRegionPropertyName = 'copyrightCountries';
  const appRoutes = await getRoutes();

  const pageUrlParams = new URLSearchParams({
    'populate[0]': 'meta',
    'populate[1]': 'title',
    'populate[2]': 'subTitle',
    'populate[3]': 'contactUsForm',
    'populate[4]': 'contactUsFormSuccessMessage',
    'populate[5]': 'contactUsFormErrorMessage',
  });

  const regionsUrlParams = {
    'populate[0]': 'nameTranslations',
    'populate[1]': countryRegionPropertyName,
    'populate[2]': `${countryRegionPropertyName}.nameTranslations`,
  };

  const copyrightLandingUrlParams = new URLSearchParams({
    'populate[0]': 'country',
    'populate[1]': 'metas',
    'pagination[start]': '0',
    'pagination[limit]': '-1',
  });

  const landingsResult = await fetchStrapi<any>(
    `/${entityBaseName}-landings`,
    copyrightLandingUrlParams,
    buildApiKey
  );
  const landings: IGenericServiceLanding[] =
    flatStrapiCollection<IGenericServiceLanding>(landingsResult);

  if (landings.length > 0) {
    landings.forEach((x) => {
      x.country = flatStrapiObject(x.country as any);
    });
  }

  const page = (
    await fetchStrapi<any>(
      `/${entityBaseName}-country-list-page`,
      pageUrlParams,
      buildApiKey
    )
  ).data.attributes as ICountryListPage;
  let regions = flatStrapiCollection(
    await fetchStrapi<any>(`/country-regions`, regionsUrlParams, buildApiKey)
  ) as ICountryRegion[];
  regions = regions
    .map((x) => ({
      ...x,
      [countryRegionPropertyName]: flatStrapiCollection(
        x[countryRegionPropertyName] as any
      ) as ICountry[],
    }))
    .filter((x) => x[countryRegionPropertyName].length > 0);

  const contactMethods = await getContactMethods();
  const navBarProps: INavBarProps = await populateNavBarProps(context.locale);
  const cookiesPopUpProps: ICookiesPopupProps = await populateCookiesPopUpProps(
    context.locale
  );
  const footerProps: IFooterProps = await populateFooterProps(context.locale);

  // Prepare the regions and countries
  const mapCountryRegions = (
    regions: ICountryRegion[],
    copyrightLandings: IGenericServiceLanding[],
    locale: string
  ) => {
    return regions.map((x) => ({
      name: x.nameTranslations.find((t) => t.language === locale)
        ?.text as string,
      countries: (x.copyrightCountries || []).map((c) => {
        const slug = getLocalizedSlugByCountryIso(
          copyrightLandings,
          c.iso,
          locale
        );
        return {
          name: getTranslation(c.nameTranslations, locale).text as string,
          code: c.iso,
          path: `${locale !== 'en' ? `/${locale}` : ''}/${slug}`,
        };
      }),
    }));
  };

  const countryRegions = mapCountryRegions(
    regions,
    landings,
    context.locale || 'en'
  );
  const meta = getTranslation(page.meta, context.locale);
  const alternativeLinks = getAlternateLinks('/copyright/countries', appRoutes);
  const canonicalLink = getCanonicalLink(context.locale || 'en', meta.slug);
  const fromPrices = landings.map((x) => ({
    countryCode: x.country.iso,
    price: x.fromPrice,
  }));
  const commonTranslations = await getCommonTranslations();

  navBarProps.breadcrumbsHomePageDisplayName = getTranslation(
    commonTranslations.homePageName,
    context.locale
  ).text as string;
  navBarProps.breadcrumbsCurrentPageDisplayName = getTranslation(
    page.title,
    context.locale
  ).text as string;

  const websiteSchema = generateWebsiteSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string
  );
  const customerReviewsMeta = (
    await fetchStrapi<any>(`/customer-reviews-meta`, undefined, buildApiKey)
  ).data.attributes as ICustomerReviewsMeta;
  const organizationSchema = await generateOrganizationSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string,
    customerReviewsMeta
  );
  const pageSchema: PageJSONLDSchema = {
    nodes: [...websiteSchema, ...organizationSchema],
  };

  const contactUsFormSuccessMessage = isUndefinedOrEmpty(
    page.contactUsFormSuccessMessage
  )
    ? (getTranslation(
        commonTranslations.contactUsFormSuccessMessage,
        context.locale
      ).text as string)
    : (getTranslation(page.contactUsFormSuccessMessage, context.locale)
        .text as string);
  const contactUsFormErrorMessage = isUndefinedOrEmpty(
    page.contactUsFormErrorMessage
  )
    ? (getTranslation(
        commonTranslations.contactUsFormErrorMessage,
        context.locale
      ).text as string)
    : (getTranslation(page.contactUsFormErrorMessage, context.locale)
        .text as string);

  const props: ICopyrightCountriesPageProps = {
    // Layout
    pageSchema,
    navBarProps,
    cookiesPopUpProps,
    footerProps,
    // Page
    locale: context.locale,
    meta,
    heading: {
      title: getTranslation(page.title, context.locale).text as string,
      subTitle: getTranslation(page.subTitle, context.locale).text as string,
    },
    countryRegions,
    copyrightLandings: landings,
    contactUsForm: getTranslation(page.contactUsForm, context.locale),
    contactUsFormSuccessMessage,
    contactUsFormErrorMessage,
    contactMethods,
    alternativeLinks,
    canonicalLink,
    fromPrices,
  };

  return {
    props,
  };
}

export default CopyrightCountriesPage;
